/**
 * What the shop owes its suppliers, and paying it down.
 *
 * Like the staff tabs, but the other way round: each credit purchase goes on
 * the supplier's account, each payment comes off, and the list shows who is
 * owed what. Owners and managers only.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatPKR, formatServerTime } from '@foodrun/shared';

import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';

export function SuppliersTab() {
  const [open, setOpen] = useState(null);
  const list = useQuery({ queryKey: ['store', 'suppliers'], queryFn: () => api.get('/store/suppliers') });
  if (open) return <SupplierAccount id={open} onBack={() => setOpen(null)} />;
  if (list.isLoading) return <Spinner />;
  const suppliers = list.data?.suppliers ?? [];
  return (
    <div>
      <div className="mb-3 flex items-baseline justify-between">
        <p className="text-sm text-ink-2">
          Owed to suppliers in total <span className="text-lg font-semibold tabular-nums text-ink">{formatPKR(list.data?.totalOwed ?? 0)}</span>
        </p>
      </div>
      <div className="overflow-x-auto rounded-xl border border-rule">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-ink-3">
              <th className="p-3 font-medium">Supplier</th>
              <th className="p-3 font-medium">Last bought</th>
              <th className="p-3 text-right font-medium">Open bills</th>
              <th className="p-3 text-right font-medium">We owe</th>
              <th className="p-3" aria-label="Open" />
            </tr>
          </thead>
          <tbody>
            {suppliers.map((s) => (
              <tr key={s.id} className="border-t border-rule">
                <td className="p-3 text-ink">
                  {s.name}
                  {s.phone && <div className="text-xs text-ink-3">{s.phone}</div>}
                </td>
                <td className="p-3 text-ink-2">{s.last_bought ? formatServerTime(s.last_bought) : '—'}</td>
                <td className="p-3 text-right tabular-nums text-ink-2">{s.open_invoices || '—'}</td>
                <td className={`p-3 text-right tabular-nums ${s.owed > 0 ? 'font-medium text-warn' : 'text-ink-3'}`}>{formatPKR(s.owed)}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="secondary" onClick={() => setOpen(s.id)}>
                    History
                  </Button>
                </td>
              </tr>
            ))}
            {suppliers.length === 0 && (
              <tr>
                <td colSpan={5} className="p-6 text-center text-sm text-ink-3">
                  No suppliers yet. They appear here after the first purchase slip.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SupplierAccount({ id, onBack }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['store', 'supplier', id], queryFn: () => api.get(`/store/suppliers/${id}`) });
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [note, setNote] = useState('');
  const pay = useMutation({
    mutationFn: () => api.post(`/store/suppliers/${id}/payments`, { amount: Number(amount), method, note }),
    onSuccess: () => {
      setAmount('');
      setNote('');
      qc.invalidateQueries({ queryKey: ['store', 'supplier', id] });
      qc.invalidateQueries({ queryKey: ['store', 'suppliers'] });
      qc.invalidateQueries({ queryKey: ['store', 'purchases'] });
    },
  });
  if (isLoading || !data) return <Spinner />;
  const owed = data.owed;
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Button variant="secondary" size="sm" onClick={onBack}>
          ← All suppliers
        </Button>
        <h2 className="text-lg font-semibold text-ink">{data.supplier.name}</h2>
        <span className="ml-auto text-sm text-ink-2">
          We owe <span className={`text-lg font-semibold tabular-nums ${owed > 0 ? 'text-warn' : 'text-ink'}`}>{formatPKR(owed)}</span>
        </span>
      </div>

      {owed > 0 && (
        <form
          className="mb-4 flex flex-wrap items-end gap-2 rounded-xl border border-rule bg-surface p-4"
          onSubmit={(e) => {
            e.preventDefault();
            pay.mutate();
          }}
        >
          <label className="text-sm text-ink-2">
            Pay (Rs)
            <input type="number" step="any" min="1" max={owed} value={amount} onChange={(e) => setAmount(e.target.value)} aria-label="Amount paid" className="mt-1 block h-11 w-36 rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink" />
          </label>
          <label className="text-sm text-ink-2">
            By
            <select value={method} onChange={(e) => setMethod(e.target.value)} aria-label="Paid by" className="mt-1 block h-11 rounded-lg border border-rule bg-surface-3 px-2 text-sm text-ink">
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank transfer</option>
              <option value="online">Online</option>
              <option value="cheque">Cheque</option>
            </select>
          </label>
          <label className="flex-1 text-sm text-ink-2">
            Note
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="optional" className="mt-1 block h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink" />
          </label>
          <Button type="submit" disabled={!(Number(amount) > 0) || Number(amount) > owed + 0.005} busy={pay.isPending}>
            Record payment
          </Button>
          {pay.error && (
            <p role="alert" className="w-full text-sm text-bad">
              {pay.error.message}
            </p>
          )}
        </form>
      )}

      <div className="overflow-x-auto rounded-xl border border-rule">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-ink-3">
              <th className="p-3 font-medium">When</th>
              <th className="p-3 font-medium">What</th>
              <th className="p-3 text-right font-medium">Bought</th>
              <th className="p-3 text-right font-medium">Paid</th>
              <th className="p-3 text-right font-medium">Owed after</th>
            </tr>
          </thead>
          <tbody>
            {data.history.map((h) => (
              <tr key={h.id} className="border-t border-rule">
                <td className="p-3 tabular-nums text-ink-2">{formatServerTime(h.at)}</td>
                <td className="p-3 text-ink">
                  {h.kind === 'purchase' ? (
                    <>
                      Slip {h.number} <span className="text-ink-3">· {h.lines} item{h.lines === 1 ? '' : 's'}{h.due ? ` · due ${h.due}` : ''}</span>
                    </>
                  ) : (
                    <>
                      Payment <span className="text-ink-3">· {h.method === 'bank_transfer' ? 'bank' : h.method}{h.by ? ` · ${h.by}` : ''}</span>
                    </>
                  )}
                  {h.note && <div className="text-xs text-ink-3">{h.note}</div>}
                </td>
                <td className="p-3 text-right tabular-nums text-ink-2">{h.kind === 'purchase' ? formatPKR(h.amount) : ''}</td>
                <td className="p-3 text-right tabular-nums text-good">{h.kind === 'payment' ? formatPKR(h.amount) : ''}</td>
                <td className="p-3 text-right tabular-nums text-ink">{formatPKR(h.balance)}</td>
              </tr>
            ))}
            {data.history.length === 0 && (
              <tr>
                <td colSpan={5} className="p-6 text-center text-sm text-ink-3">
                  Nothing on credit with this supplier.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
